import React, {useEffect, useState} from 'react';
import AuthenticatedLayout from '../authenticatedLayout/AuthenticatedLayout';
import {useAuth} from '../AuthContext';
import {BASE_URL, USER_ENDPOINTS} from '../../Constants';
import AddClientModal from '../addClientModal/AddClientModal';
import axios from 'axios';
import './HistoryPage.css';
import '../../App.css'

const HistoryPage = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [clientHistory, setClientHistory] = useState([]);
    const [filteredHistory, setFilteredHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const {isAuthenticated} = useAuth();
    const [showClientModal, setShowClientModal] = useState(false);
    const [selectedClient, setSelectedClient] = useState(null);
    const [loadingClient, setLoadingClient] = useState(false);

    useEffect(() => {
        // Fetch client history data from the backend
        const fetchClientHistory = async () => {
            try {
                setLoading(true);

                // Get authorization token from localStorage
                const token = localStorage.getItem('token');

                if (!token) {
                    throw new Error('No authentication token found');
                }

                // Get current date in YYYY-MM-DD format
                const today = new Date();
                const oneYearAgo = new Date();
                oneYearAgo.setFullYear(today.getFullYear() - 1);

                // Fetch events between dates
                // Using the /event endpoint from EventController in the backend
                const response = await fetch(`${BASE_URL}/event/all`, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                });

                if (!response.ok) {
                    throw new Error(`HTTP error! Status: ${response.status}`);
                }

                const data = await response.json();

                // Transform and prepare data for display
                const historyData = data.map(event => {
                    return {
                        id: event.id,
                        date: event.scheduledDate,
                        name: event.customerEntity?.lastName + ' ' + event.customerEntity?.firstName || event.name || 'N/A',
                        phone: event.customerEntity?.phone1 || 'N/A',
                        mobilierType: event.customerEntity?.furnitureType || 'N/A',
                        eventType: event.eventType || 'N/A',
                        customerId: event.customerEntity?.id || null
                    };
                });

                setClientHistory(historyData);
                setFilteredHistory(historyData);
                setError(null);
            } catch (err) {
                console.error('Error fetching client history:', err);
                setError('Nu s-au putut încărca datele istoricului. Vă rugăm încercați din nou.');
            } finally {
                setLoading(false);
            }
        };

        if (isAuthenticated) {
            fetchClientHistory();
        }
    }, [isAuthenticated]);

    useEffect(() => {
        // Filter the client history based on search term
        if (searchTerm.trim() === '') {
            setFilteredHistory(clientHistory);
        } else {
            const lowercasedTerm = searchTerm.toLowerCase();
            const filtered = clientHistory.filter(item => {
                // Format date for search
                const formattedDate = item.date ? formatDate(item.date).toLowerCase() : '';

                return (
                    (item.name && item.name.toLowerCase().includes(lowercasedTerm)) ||
                    (item.phone && item.phone.toLowerCase().includes(lowercasedTerm)) ||
                    (item.mobilierType && item.mobilierType.toLowerCase().includes(lowercasedTerm)) ||
                    (item.eventType && item.eventType.toLowerCase().includes(lowercasedTerm)) ||
                    (item.date && item.date.includes(lowercasedTerm)) ||
                    (formattedDate && formattedDate.includes(lowercasedTerm))
                );
            });
            setFilteredHistory(filtered);
        }
    }, [searchTerm, clientHistory]);

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };

    // Format date from YYYY-MM-DD to DD.MM.YYYY
    const formatDate = (dateString) => {
        if (!dateString) return '';
        const parts = dateString.split('-');
        return `${parts[2]}.${parts[1]}.${parts[0]}`;
    };

    // Helper function to map contract type from API format to display format
    const getContractType = (activityType) => {
        switch (activityType) {
            case 'CONTRACT':
            case 'CONTRACT_NESEMNAT':
                return 'Contract nesemnat';
            case 'CONTRACT_SEMNAT':
                return 'Contract semnat';
            case 'IN_OFERTARE':
                return 'In ofertare';
            case 'FARA_CONTRACT':
                return 'Fara contract';
            default:
                return '';
        }
    };

    // Handle clicking on a history item to open client details
    const handleItemClick = async (item) => {
        if (!item.customerId) return;

        try {
            setLoadingClient(true);
            const token = localStorage.getItem('token');

            // Fetch full client data
            const response = await axios.get(`${USER_ENDPOINTS.GET_CUSTOMER}/${item.customerId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            const clientData = response.data;

            // Fetch associated events (measurements and installations)
            let measurements = [];
            let installations = [];

            try {
                const measurementResponse = await axios.get(`${USER_ENDPOINTS.GET_EVENTS}/${item.customerId}`, {
                    params: {eventType: 'MASURATORI'},
                    headers: {'Authorization': `Bearer ${token}`}
                });
                measurements = measurementResponse.data.map(event => ({
                    id: event.id,
                    date: event.scheduledDate,
                    startTime: event.startTime ? event.startTime.substring(0, 5) : '09:00',
                    endTime: event.endTime ? event.endTime.substring(0, 5) : '10:00',
                    description: event.description || 'Măsurătoare',
                    eventType: 'MASURATORI'
                }));
            } catch (e) {
                console.log('No measurements found');
            }

            try {
                const installationResponse = await axios.get(`${USER_ENDPOINTS.GET_EVENTS}/${item.customerId}`, {
                    params: {eventType: 'MONTAJ'},
                    headers: {'Authorization': `Bearer ${token}`}
                });
                installations = installationResponse.data.map(event => ({
                    id: event.id,
                    date: event.scheduledDate,
                    startTime: event.startTime ? event.startTime.substring(0, 5) : '09:00',
                    endTime: event.endTime ? event.endTime.substring(0, 5) : '10:00',
                    description: event.description || 'Montaj',
                    eventType: 'MONTAJ'
                }));
            } catch (e) {
                console.log('No installations found');
            }

            const formattedClientData = {
                id: clientData.id,
                firstName: clientData.firstName || '',
                lastName: clientData.lastName || '',
                email: clientData.email || '',
                phone1: clientData.phone1 || '',
                phone2: clientData.phone2 || '',
                address: clientData.homeAddress || '',
                furnitureType: clientData.furnitureType || '',
                cnp: clientData.cnp || '',
                idSeries: clientData.serialNumber || '',
                details: clientData.details || '',
                advance: clientData.advance || (clientData.initialPayment ? clientData.initialPayment.toString() : ''),
                total: clientData.total || (clientData.totalAmount ? clientData.totalAmount.toString() : ''),
                rest: clientData.totalAmount && clientData.initialPayment
                    ? (clientData.totalAmount - clientData.initialPayment).toString()
                    : '',
                clientType: clientData.clientType,
                companyName: clientData.companyName || '',
                cui: clientData.cui || '',
                companyAddress: clientData.companyAddress || '',
                contract: getContractType(clientData.activity),
                measurements: measurements,
                installations: installations,
                initialPaymentType: clientData.initialPaymentType,
                remainingPaymentType: clientData.remainingPaymentType,
                isRemainingPaymentPaid: clientData.isRemainingPaymentPaid
            };

            setSelectedClient(formattedClientData);
            setShowClientModal(true);

        } catch (error) {
            console.error('Error fetching client data:', error);
        } finally {
            setLoadingClient(false);
        }
    };

    const handleClientModalClose = () => {
        setShowClientModal(false);
        setSelectedClient(null);
    };

    const handleClientSave = () => {
        setShowClientModal(false);
        setSelectedClient(null);
    };

    return (
        <AuthenticatedLayout>
            <div className="istoric-container">
                <div className="header-container">
                    <h1 className="page-title">Istoric</h1>
                </div>
                <div className="search-container">
                    <div className="search-input-container">
                        <input
                            type="text"
                            placeholder="Caută lucrare"
                            value={searchTerm}
                            onChange={handleSearchChange}
                            className="search-input"
                        />
                    </div>
                </div>

                {loading ? (
                    <div className="loading-message">Se încarcă...</div>
                ) : error ? (
                    <div className="error-message">{error}</div>
                ) : (
                    <div className="istoric-content">
                        {filteredHistory.length > 0 ? (
                            <div className="istoric-list">
                                {filteredHistory.map((item, index) => (
                                    <div
                                        key={item.id || index}
                                        className={`istoric-item ${item.customerId ? 'clickable' : ''}`}
                                        onClick={() => handleItemClick(item)}
                                    >
                                        <div className="istoric-item-numbering">{index + 1}.</div>
                                        <div className="istoric-item-main">
                                            <div className="istoric-item-row">
                                                <div className="istoric-item-name">{item.name}</div>
                                                <div className="istoric-item-phone">
                                                    <span className="phone-label">Telefon:</span> {item.phone}
                                                </div>
                                            </div>
                                            <div className="istoric-item-row second-row">
                                                <div className="istoric-item-type">
                                                    <span
                                                        className="type-label">Tip mobilier:</span> {item.mobilierType}
                                                </div>
                                                <div className="istoric-item-type">
                                                    <span className="type-label">Tip eveniment:</span> {item.eventType}
                                                </div>
                                                <div className="istoric-item-date">
                                                    <span className="date-label">Data:</span> {formatDate(item.date)}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="no-results">Nu s-au găsit rezultate pentru căutarea dumneavoastră.</div>
                        )}
                    </div>
                )}

                {loadingClient && (
                    <div style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0.3)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        zIndex: 999
                    }}>
                        <div style={{
                            backgroundColor: 'white',
                            padding: '20px 40px',
                            borderRadius: '8px',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
                        }}>
                            Se încarcă datele clientului...
                        </div>
                    </div>
                )}

                <AddClientModal
                    isOpen={showClientModal}
                    onClose={handleClientModalClose}
                    onSave={handleClientSave}
                    initialData={selectedClient}
                />
            </div>
        </AuthenticatedLayout>
    );
};

export default HistoryPage;
